import { Box, Button, Typography, makeStyles } from "@material-ui/core";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import GetAppIcon from "@mui/icons-material/GetApp";
import { utils, writeFileXLSX } from "xlsx";

import themeColorsDesingSystem from "assets/theme/ds-colors";
import { IsMobile } from "utils/is-mobile";

import { formatNumber } from "./utils";

const useStyles = makeStyles({
  card: {
    backgroundColor: themeColorsDesingSystem.background.main,
    borderRadius: 4,
    boxShadow: "0 1px 3px rgba(0,0,0,0.08)",
    border: `1px solid ${themeColorsDesingSystem.stone.main}`,
    padding: "1.25rem",
    marginBottom: "2rem",
  },
  header: {
    display: "flex",
    alignItems: "center",
    justifyContent: "space-between",
    marginBottom: "1rem",
    paddingBottom: "0.75rem",
    borderBottom: `1px solid ${themeColorsDesingSystem.stone.main}`,
  },
  title: {
    fontSize: "0.9375rem",
    fontWeight: 700,
    color: themeColorsDesingSystem.gray.dark,
    display: "flex",
    alignItems: "center",
    gap: 8,
  },
  exportButton: {
    fontSize: "0.75rem",
    fontWeight: 600,
    textTransform: "none",
    color: themeColorsDesingSystem.green.main,
    "&:hover": {
      backgroundColor: themeColorsDesingSystem.green.background,
    },
  },
  table: {
    width: "100%",
    fontSize: "0.875rem",
    borderCollapse: "collapse",
    whiteSpace: "nowrap",
  },
  th: {
    padding: "0.75rem 1rem",
    fontWeight: 700,
    color: themeColorsDesingSystem.gray.dark,
    textAlign: "center",
    borderBottom: `2px solid ${themeColorsDesingSystem.stone.main}`,
  },
  thFirst: {
    textAlign: "left",
    paddingRight: "1rem",
  },
  td: {
    padding: "0.75rem 1rem",
    textAlign: "center",
    color: themeColorsDesingSystem.text.primary,
    fontWeight: 600,
    borderBottom: `1px solid ${themeColorsDesingSystem.stone.light}`,
  },
  tdFirst: {
    textAlign: "left",
    color: themeColorsDesingSystem.gray.dark,
    fontWeight: 500,
    paddingRight: "1rem",
  },
  row: {
    transition: "background-color 0.15s",
    "&:hover": {
      backgroundColor: themeColorsDesingSystem.stone.light,
      "& $td": {
        color: themeColorsDesingSystem.primary.main,
      },
    },
  },
});

function exportMonthlyHistoryToXlsx({
  months,
  indicadores,
  startDate,
  endDate,
}) {
  const headerRow = ["Indicador", ...months];
  const dataRows = indicadores.map((indicador) => [
    indicador.Indicador ?? "",
    ...months.map((mes) => indicador.ValoresPorMes?.[mes] ?? 0),
  ]);

  const worksheet = utils.aoa_to_sheet([headerRow, ...dataRows]);
  const workbook = utils.book_new();
  utils.book_append_sheet(workbook, worksheet, "Historico Mensal");

  const periodSuffix = startDate && endDate ? `_${startDate}_${endDate}` : "";
  writeFileXLSX(
    workbook,
    `historico-mensal-indicadores-acesso${periodSuffix}.xlsx`,
  );
}

export default function MonthlyHistoryTable({
  months = [],
  indicadores = [],
  startDate,
  endDate,
  onExportError,
}) {
  const classes = useStyles();
  const isMobile = IsMobile();

  const handleExport = () => {
    if (!months.length || !indicadores.length) {
      onExportError?.("Não há dados para exportar.");
      return;
    }

    try {
      exportMonthlyHistoryToXlsx({ months, indicadores, startDate, endDate });
    } catch (error) {
      onExportError?.(
        error instanceof Error
          ? error.message
          : "Erro ao exportar histórico mensal.",
      );
    }
  };

  if (!months?.length) return null;

  return (
    <Box className={classes.card}>
      <Box className={classes.header}>
        <Typography className={classes.title}>
          <CalendarTodayIcon
            style={{
              fontSize: 18,
              color: themeColorsDesingSystem.primary.main,
            }}
          />
          Histórico Mensal - Período Selecionado
        </Typography>
        <Button
          className={classes.exportButton}
          startIcon={<GetAppIcon style={{ fontSize: 14 }} />}
          onClick={handleExport}
          disabled={!indicadores.length}
          style={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
          }}
        >
          Exportar XLSX
        </Button>
      </Box>

      <Box style={{ overflowX: "auto" }}>
        <table className={classes.table}>
          <thead>
            <tr>
              <th className={`${classes.th} ${classes.thFirst}`}>Indicador</th>
              {months.map((mes) => (
                <th key={mes} className={classes.th}>
                  {mes}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>
            {indicadores.map((indicador) => (
              <tr
                key={indicador.Chave || indicador.Indicador}
                className={classes.row}
              >
                <td
                  className={`${classes.td} ${classes.tdFirst}`}
                  style={{ fontSize: isMobile ? "0.75rem" : "0.875rem" }}
                >
                  {indicador.Indicador}
                </td>
                {months.map((mes) => (
                  <td key={mes} className={classes.td}>
                    {formatNumber(indicador.ValoresPorMes?.[mes]) ?? "0"}
                  </td>
                ))}
              </tr>
            ))}
          </tbody>
        </table>
      </Box>
    </Box>
  );
}
